import React, { useState } from 'react';
import { AnimatedSection } from '../components/ui/AnimatedSection';
import { SectionHeading } from '../components/ui/SectionHeading';
import { Button } from '../components/ui/Button';
import { motion, AnimatePresence } from 'framer-motion';
import { ActivityIcon, DropletIcon, UtensilsIcon, DumbbellIcon, BrainCircuitIcon, PillIcon, MessageSquareIcon, CheckCircle2Icon, XIcon, ChevronDownIcon } from 'lucide-react';
export function LongevityProgram() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const inclusions = [{
    icon: DropletIcon,
    title: 'Advanced Blood Work Analysis',
    desc: 'Comprehensive panel examining hormones, metabolic markers, and nutrient deficiencies.'
  }, {
    icon: ActivityIcon,
    title: 'Metabolic Assessment',
    desc: 'Deep dive into your current metabolic state, insulin sensitivity, and energy systems.'
  }, {
    icon: UtensilsIcon,
    title: 'Customized Nutrition Protocol',
    desc: 'Data-driven dietary plan designed to restore balance and fuel performance.'
  }, {
    icon: DumbbellIcon,
    title: 'Strength Training Programming',
    desc: 'Structured workouts focused on building lean muscle and metabolic resilience.'
  }, {
    icon: BrainCircuitIcon,
    title: 'Lifestyle Optimization',
    desc: 'Protocols for sleep, stress management, and daily habits.'
  }, {
    icon: PillIcon,
    title: 'Supplementation Strategy',
    desc: 'Targeted, high-quality supplements based strictly on your lab results.'
  }, {
    icon: MessageSquareIcon,
    title: 'Continuous Coaching',
    desc: 'Ongoing support, adjustments, and accountability to ensure lasting results.'
  }];
  const outcomes = ['Reduce body fat and optimize body composition', 'Increase lean muscle mass and strength', 'Restore insulin sensitivity and metabolic flexibility', 'Improve hormonal balance naturally', 'Increase daily energy, focus, and mental clarity', 'Extend healthspan and mitigate chronic disease risk'];
  const faqs = [{
    q: 'How is BioShift different from hiring a personal trainer or nutritionist?',
    a: "BioShift is a complete human restoration system. We don't just write workouts or meal plans. We analyze your blood work, assess your metabolism, build custom protocols, recalibrate your mindset, and provide continuous coaching. It's the difference between treating symptoms and fixing the root cause."
  }, {
    q: "I've tried many programs before. Why would this one work?",
    a: "Because we don't guess. Every decision is driven by your data — blood work, metabolic markers, and biofeedback. We address the biological, psychological, and lifestyle factors simultaneously. Most programs only touch one piece. We restore the entire system."
  }, {
    q: 'How much does the program cost?',
    a: 'The Longevity Program is a premium investment in your health and longevity. Exact pricing is discussed during your complimentary strategy call, where we assess your specific needs and goals. We believe in full transparency once we understand your situation.'
  }, {
    q: 'Do I need to be in Dallas to participate?',
    a: "No. While we're based in Dallas, Texas, the program is delivered entirely online. We work with clients nationwide through virtual coaching, digital protocols, and remote monitoring."
  }, {
    q: 'What kind of time commitment is required?',
    a: 'Expect to dedicate 4-6 hours per week to training, meal preparation, and coaching calls. The program is designed for busy professionals — we build it around your schedule, not the other way around.'
  }, {
    q: "What if I'm not sure I'm ready?",
    a: "That's exactly what the strategy call is for. There's no obligation. We'll assess your situation, discuss your goals, and determine together if BioShift is the right fit. If it's not, we'll tell you honestly."
  }];
  return <main className="w-full pt-24" data-id="element-829">
      {/* Hero */}
      <section className="py-20 md:py-32 bg-dark relative border-b border-border overflow-hidden" data-id="element-830">
        <div className="absolute inset-0 z-0 opacity-30" data-id="element-831">
          <img src="https://images.unsplash.com/photo-1581009146145-b5ef050c2e1e?q=80&w=2070" alt="Training" className="w-full h-full object-cover" data-id="element-832" />
          <div className="absolute inset-0 bg-gradient-to-t from-dark via-dark/80 to-transparent" data-id="element-833"></div>
        </div>
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10" data-id="element-834">
          <AnimatedSection data-id="element-835">
            <span className="text-gold text-sm font-medium uppercase tracking-[0.3em] mb-6 block" data-id="element-836">
              6-Month Metabolic Transformation
            </span>
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 font-heading leading-tight" data-id="element-837">
              BioShift Longevity Program
            </h1>
            <p className="text-xl text-muted leading-relaxed max-w-2xl mx-auto mb-10" data-id="element-838">
              A premium, high-touch coaching experience designed to restore
              metabolic health, rebuild your body, and optimize your performance
              for the decades ahead.
            </p>
            <Button href="https://holistic-health.bioshift.net/calender-booking" size="lg" data-id="element-839">
              Apply for the Program
            </Button>
          </AnimatedSection>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-24 md:py-32 bg-surface" data-id="element-840">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-841">
          <AnimatedSection data-id="element-842">
            <SectionHeading title="What's Included" overline="The Protocol" data-id="element-843" />
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-16" data-id="element-844">
            {inclusions.map((item, i) => <AnimatedSection key={i} delay={i * 0.1} data-id="element-845">
                <div className="bg-dark border border-border p-8 h-full hover:border-gold/30 transition-colors" data-id="element-846">
                  <item.icon className="w-10 h-10 text-gold mb-6" data-id="element-847" />
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-848">
                    {item.title}
                  </h3>
                  <p className="text-muted text-sm leading-relaxed" data-id="element-849">
                    {item.desc}
                  </p>
                </div>
              </AnimatedSection>)}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
                    WHO THIS IS FOR (NEW)
                ═══════════════════════════════════════════════════════════════ */}
      <section className="py-24 md:py-32 bg-dark" data-id="element-850">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-851">
          <AnimatedSection data-id="element-852">
            <SectionHeading overline="IDEAL CLIENT" title="Is the Longevity Program Right for You?" data-id="element-853" />
          </AnimatedSection>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mt-16" data-id="element-854">
            {/* Left */}
            <AnimatedSection direction="right" data-id="element-855">
              <div className="bg-surface border border-border p-10 h-full" data-id="element-856">
                <h3 className="text-2xl font-bold text-white mb-8 font-heading" data-id="element-857">
                  This program is designed for:
                </h3>
                <ul className="space-y-6" data-id="element-858">
                  {['Professionals and executives earning $150K+ who want elite-level health coaching', 'Individuals struggling with weight gain, fatigue, or hormonal imbalances despite trying multiple approaches', 'High-performers who want data-driven, root-cause solutions — not generic meal plans', 'People ready to commit to 6 months of structured transformation', 'Leaders who understand that health is the foundation of sustained success'].map((item, i) => <li key={i} className="flex items-start" data-id="element-859">
                      <CheckCircle2Icon className="w-6 h-6 text-gold mr-4 flex-shrink-0 mt-0.5" data-id="element-860" />
                      <span className="text-white leading-relaxed" data-id="element-861">{item}</span>
                    </li>)}
                </ul>
              </div>
            </AnimatedSection>

            {/* Right */}
            <AnimatedSection direction="left" data-id="element-862">
              <div className="bg-surface border border-border p-10 h-full" data-id="element-863">
                <h3 className="text-2xl font-bold text-white/60 mb-8 font-heading" data-id="element-864">
                  This program is NOT for:
                </h3>
                <ul className="space-y-6" data-id="element-865">
                  {['People looking for a quick-fix diet or 30-day challenge', 'Anyone not willing to invest time, effort, and resources into their health', 'Individuals who want to be told what to do without understanding why'].map((item, i) => <li key={i} className="flex items-start" data-id="element-866">
                      <XIcon className="w-6 h-6 text-white/30 mr-4 flex-shrink-0 mt-0.5" data-id="element-867" />
                      <span className="text-muted leading-relaxed" data-id="element-868">{item}</span>
                    </li>)}
                </ul>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="py-24 md:py-32 bg-surface" data-id="element-869">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-870">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-871">
            <AnimatedSection direction="right" data-id="element-872">
              <SectionHeading overline="The Results" title="What You Will Achieve" align="left" description="This isn't a quick fix. It's a fundamental recalibration of your biology." data-id="element-873" />
              <ul className="mt-10 space-y-6" data-id="element-874">
                {outcomes.map((outcome, i) => <li key={i} className="flex items-start" data-id="element-875">
                    <CheckCircle2Icon className="w-6 h-6 text-gold mr-4 flex-shrink-0 mt-0.5" data-id="element-876" />
                    <span className="text-white text-lg" data-id="element-877">{outcome}</span>
                  </li>)}
              </ul>
            </AnimatedSection>

            <AnimatedSection direction="left" data-id="element-878">
              <div className="bg-dark p-10 border border-gold/20 relative" data-id="element-879">
                <div className="absolute -top-4 -left-4 w-8 h-8 border-t-2 border-l-2 border-gold" data-id="element-880"></div>
                <div className="absolute -bottom-4 -right-4 w-8 h-8 border-b-2 border-r-2 border-gold" data-id="element-881"></div>
                <h3 className="text-2xl font-heading text-white mb-6" data-id="element-882">
                  The Commitment
                </h3>
                <p className="text-muted mb-6 leading-relaxed" data-id="element-883">
                  This program requires dedication. We provide the science, the
                  structure, and the accountability, but you must do the work.
                  It is designed for high-performers who are ready to stop
                  guessing and start executing.
                </p>
                <div className="pt-6 border-t border-border" data-id="element-884">
                  <div className="flex justify-between items-center mb-2" data-id="element-885">
                    <span className="text-white font-medium" data-id="element-886">Duration</span>
                    <span className="text-gold" data-id="element-887">6 Months</span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-888">
                    <span className="text-white font-medium" data-id="element-889">Format</span>
                    <span className="text-gold" data-id="element-890">1-on-1 Online Coaching</span>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
                    INVESTMENT FRAMING (NEW)
                ═══════════════════════════════════════════════════════════════ */}
      <section className="py-24 md:py-32 bg-surface" data-id="element-891">
        <div className="max-w-3xl mx-auto px-6 text-center" data-id="element-892">
          <AnimatedSection data-id="element-893">
            <span className="text-gold text-sm font-medium uppercase tracking-[0.3em] mb-4 block" data-id="element-894">
              THE INVESTMENT
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-8 font-heading" data-id="element-895">
              What's the Value of Your Health?
            </h2>
            <div className="space-y-6 text-muted text-lg leading-relaxed mb-12" data-id="element-896">
              <p data-id="element-897">
                Consider what metabolic dysfunction is already costing you: lost
                productivity, medical bills, low energy, missed opportunities,
                and years off your life.
              </p>
              <p data-id="element-898">
                The BioShift Longevity Program is a premium, high-touch coaching
                experience. It is priced to reflect the depth of expertise,
                personalization, and accountability you receive.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
                    FAQ (NEW)
                ═══════════════════════════════════════════════════════════════ */}
      <section className="py-24 md:py-32 bg-dark" data-id="element-899">
        <div className="max-w-3xl mx-auto px-6" data-id="element-900">
          <AnimatedSection data-id="element-901">
            <SectionHeading overline="QUESTIONS" title="Frequently Asked Questions" data-id="element-902" />
          </AnimatedSection>

          <div className="mt-16" data-id="element-903">
            {faqs.map((faq, index) => <AnimatedSection key={index} delay={index * 0.1} data-id="element-904">
                <div className="border-b border-border py-6" data-id="element-905">
                  <button onClick={() => setOpenFaq(openFaq === index ? null : index)} className="w-full flex justify-between items-center text-left focus:outline-none group" data-id="element-906">
                    <span className="text-white font-bold text-lg pr-8 group-hover:text-gold transition-colors" data-id="element-907">
                      {faq.q}
                    </span>
                    <ChevronDownIcon className={`w-5 h-5 text-gold flex-shrink-0 transition-transform duration-300 ${openFaq === index ? 'rotate-180' : ''}`} data-id="element-908" />
                  </button>
                  <AnimatePresence data-id="element-909">
                    {openFaq === index && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.3
                }} className="overflow-hidden" data-id="element-910">
                        <p className="text-muted leading-relaxed mt-4 pb-2" data-id="element-911">
                          {faq.a}
                        </p>
                      </motion.div>}
                  </AnimatePresence>
                </div>
              </AnimatedSection>)}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
                    FINAL CTA (MODIFIED)
                ═══════════════════════════════════════════════════════════════ */}
      <section className="py-32 bg-surface border-t border-gold relative overflow-hidden" data-id="element-912">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(212,175,55,0.05)_0%,transparent_70%)]" data-id="element-913"></div>
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10" data-id="element-914">
          <AnimatedSection data-id="element-915">
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 font-heading leading-tight" data-id="element-916">
              This Is Your Moment
            </h2>
            <p className="text-xl text-muted mb-12 max-w-2xl mx-auto leading-relaxed" data-id="element-917">
              Every day you wait is another day your metabolism works against
              you. Apply now and take the first step toward the health, energy,
              and performance you deserve.
            </p>
            <Button href="https://holistic-health.bioshift.net/calender-booking" size="lg" className="mb-6" data-id="element-918">
              Apply for Your Strategy Call
            </Button>
            <p className="text-xs text-muted uppercase tracking-wider block" data-id="element-919">
              Limited to 15 new clients per quarter
            </p>
          </AnimatedSection>
        </div>
      </section>
    </main>;
}